(ns bleene.dag.nodes
  (:require
   ["humanize-duration" :as humanizeDuration]
   [applied-science.js-interop :as jsi]
   [bleene.dag.commons :as commons]
   [bleene.dag.connections :as connections]
   [bleene.dag.dag-context :as dag-context]
   [bleene.layout.theme :refer [dag-palette]]
   [bleene.utils :as utils]
   [goog.functions :refer [throttle]]
   [minorodata.app.utils :as app.utils]
   [minorodata.utils :as minoro.utils]
   [re-frame.core :as re-frame]
   [re-posh.core :as re-posh]
   [react :as react]
   [react-konva :refer [Circle Group Image Rect Text]]
   [ui.tooltip :as tooltip]))


;; clj-kondo doesn't want to notice Konva in requires somehow
(declare Konva)


(defn stop-move [event id]
  (let [target   (jsi/get event :target)
        position {:x (jsi/call target :x)
                  :y (jsi/call target :y)}]
    (re-frame/dispatch [:dag/set-node-position id position])))


(defn on-move [event index id]
  (let [target (jsi/get event :target)
        x      (jsi/call target :x)
        y      (jsi/call target :y)]
    (re-frame/dispatch [:dag/move-node-connections id {:x x :y y}])
    (jsi/call target :zIndex index)))


(defn unselect-node [dag-id]
  (re-frame/dispatch [:dag/select-node dag-id nil]))


(defonce transform-icon
  (new js/Image))
(jsi/assoc! transform-icon :src "/icons/transform.svg")


(defn clip-fn [{:keys [x y width height radius]
                :or   {x 0 y 0 width 100 height 100 radius 4}}]
  (fn [ctx]
    (doto ctx
      (jsi/call :beginPath)
      (jsi/call :moveTo (+ x radius) y)
      (jsi/call :lineTo (+ x width (- radius)) y)
      (jsi/call :quadraticCurveTo (+ x width) y (+ x width) (+ y radius))
      (jsi/call :lineTo (+ x width) (+ y height (- radius)))
      (jsi/call :quadraticCurveTo (+ x width) (+ y height) (+ x width (- radius)) (+ y height))
      (jsi/call :lineTo (+ x radius) (+ y height))
      (jsi/call :quadraticCurveTo x (+ y height) x (+ y height (- radius)))
      (jsi/call :lineTo x (+ y radius))
      (jsi/call :quadraticCurveTo x y (+ x radius) y)
      (jsi/call :closePath))))

(defn clip-right-fn [{:keys [x y width height radius]
                      :or   {x 0 y 0 width 100 height 100 radius 4}}]
  (fn [ctx]
    (doto ctx
      (jsi/call :beginPath)
      (jsi/call :moveTo (+ x radius) y)
      (jsi/call :lineTo (+ x width) y)
      (jsi/call :lineTo (+ x width) (+ y height))
      (jsi/call :lineTo (+ x radius) (+ y height))
      (jsi/call :quadraticCurveTo x (+ y height) x (+ y height (- radius)))
      (jsi/call :lineTo x (+ y radius))
      (jsi/call :quadraticCurveTo x y (+ x radius) y)
      (jsi/call :closePath))))


(defn scale-out [event]
  (jsi/call-in event [:target :scale] #js {:x 2 :y 2}))


(defn scale-in [event]
  (jsi/call-in event [:target :scale] #js {:x 1 :y 1}))


(defn draw-temp-edge [event dag-id id position]
  (jsi/assoc! event :cancelBubble true)
  (jsi/assoc! Konva :hitOnDragEnabled true)
  (connections/add-temp-edge dag-id id position))


(defn update-temp-edge [dag-id stage position]
  (let [point-x (jsi/get position :x)
        point-y (jsi/get position :y)
        scale   (jsi/call stage :scaleX)
        stage-x (jsi/call stage :x)
        stage-y (jsi/call stage :y)
        x       (/ (- point-x stage-x) scale)
        y       (/ (- point-y stage-y) scale)]
    (connections/update-temp-edge dag-id {:x x :y y})))


(defn reset-temp-edge [event dag-id]
  (jsi/assoc! event :cancelBubble true)
  (jsi/assoc! Konva :hitOnDragEnabled false)
  (connections/reset-edge dag-id))


(defn input-circle [{:keys [selected?]}]
  (let [{:keys [dag-id]} (utils/context dag-context/context)
        {:keys [scale node-height target-circle-offset-x]}
        (utils/query {:scale                  [:dag dag-id :scale]
                      :node-height            [:dag-params :node-height]
                      :target-circle-offset-x [:dag-params :target-circle-offset-x]})]
    [:> Circle {:x       (- target-circle-offset-x)
                :y       (/ node-height 2)
                :width   (+ 6 (/ 6 scale))
                :height  (+ 6 (/ 6 scale))
                :opacity (if selected? 0.9 0.4)
                :fill    "#429777"}]))


(defn connection-circle [{:keys [id selected?]}]
  (let [{:keys [dag-id]} (utils/context dag-context/context)
        {:keys [scale node-width node-height target-circle-offset-x]}
        (utils/query {:scale                  [:dag dag-id :scale]
                      :node-width             [:dag-params :node-width]
                      :node-height            [:dag-params :node-height]
                      :target-circle-offset-x [:dag-params :target-circle-offset-x]})
        circle-x   (+ node-width target-circle-offset-x)
        circle-y   (/ node-height 2)
        circle-ref (react/useRef nil)]
    [:> Circle {:ref           circle-ref
                :x             circle-x
                :y             circle-y
                :width         (+ 6 (/ 6 scale))
                :height        (+ 6 (/ 6 scale))
                :opacity       (if selected? 0.9 0.4)
                :fill          "#3366FF"

                :on-mouseenter scale-out
                :on-mouseleave scale-in

                :draggable     true
                :on-drag-start #(draw-temp-edge % dag-id id {:x node-width :y circle-y})
                :on-drag-move  (fn [event]
                                 (jsi/assoc! event :cancelBubble true))
                :dragBoundFunc (fn [position _event]
                                 (let [stage (jsi/call-in circle-ref [:current :getStage])]
                                   (update-temp-edge dag-id stage position))
                                 (jsi/call-in circle-ref [:current :getAbsolutePosition]))
                :on-drag-end   #(reset-temp-edge % dag-id)}]))


(defn move-grouped-nodes [event id x y]
  (let [new-x (jsi/call-in event [:target :x])
        new-y (jsi/call-in event [:target :y])]
    (re-frame/dispatch-sync [:dag/move-group-nodes id (- new-x x) (- new-y y)])))


(defn select-group [event dag-id group-id]
  (let [stage       (jsi/call-in event [:target :getStage])
        click-event (str "click." (name group-id))]
    (re-frame/dispatch [:dag/select-group dag-id group-id])

    (jsi/call stage :on click-event
      (fn [new-event]
        (let [target (jsi/get new-event :target)
              group  (if (= (jsi/call target :name) "nodes-group")
                       target
                       (jsi/call target :findAncestor ".nodes-group"))]
          (when (or (nil? group)
                    (not= (jsi/call group :id) (name group-id)))
            (re-frame/dispatch-sync [:dag/select-group dag-id nil])
            (jsi/call stage :off click-event)))))))


(defn open-node-context [event node-id & params]
  (commons/open-context-menu event {:type :node :id node-id} params))


(defn open-group-context [event group-id]
  (commons/open-context-menu event {:type :group :id group-id}))


(defn group [{:keys [id params]}]
  (let [{:keys [dag-id]} (utils/context dag-context/context)
        {:keys [name nodes type scale selected-group node-width node-height raw-groups edges extracts]}
        (utils/query {:raw-groups     [:raw-groups]
                      :name           [:groups id :name]
                      :nodes          [:groups id :nodes]
                      :type           [:groups id :type]
                      :scale          [:dag dag-id :scale]
                      :selected-group [:dag dag-id :selected-group]
                      :node-width     [:dag-params :node-width]
                      :node-height    [:dag-params :node-height]
                      :edges          [:dag dag-id :edges]
                      :extracts       [:raw-data :extracts]})

        {:keys [x y width height]}
        ;; (get raw-groups id)
        @(re-frame/subscribe [:dag/group-rect dag-id id])

        text-scale   (min
                      (if (<= 1 scale)
                        1
                        (/ 1 scale))
                      2)
        selected?    (= id selected-group)
        empty-group? (seq nodes)

        transform-group? (= type :group)]

    (when empty-group?
      [:> Group {:x           x
                 :y           y
                 :id          id
                 :name        "nodes-group"
                 :on-click    #(select-group % dag-id id)
                 :draggable   selected?
                 :on-drag-end #(move-grouped-nodes % id x y)}
       [:> Rect {:width        width
                 :height       height
                 :opacity      1
                 :cornerRadius 12
                 :fill         (if transform-group? "#F3F2F8" "#1A0F62")
                 :stroke       (if transform-group? "#E4E4E7" "#1A0F62" )}]

       (when selected?
         [:> Rect {:width        width
                   :height       height
                   :cornerRadius 12
                   :fill         (if transform-group? "#F3F2F8" "#1A0F62")
                   :stroke       "#9BCDEA"}])


       [:> Text {:text      name
                 :name      "nodes-group-title"
                 :font-size (* 24 text-scale)
                 :offset-y  (/ -36 text-scale)
                 :offset-x  -30
                 :font-style 600
                 :ellipsis  true
                 :wrap      "char"
                 :width     (- width 30)
                 :height    20
                 :align     "left"
                 :fill      (if transform-group? "#000" "#fff")}]

       (when (and (seq nodes) (= type :source) (> scale 0.5))
         (let [source-extracts (count (filter #(= id (:source-id %)) extracts))
               unconnected (- source-extracts (count nodes))]
           (when (pos? unconnected)
             [:> Text {:text       (str unconnected " other unconnected extracts")
                       :name       "unconnected-nodes-count"
                       :offset-y   (+ (* -1 height) 32)
                       :offset-x   -30
                       :font-size  16
                       :font-style 400
                       :width      (- width 30)
                       :height     20
                       :align      "left"
                       :fill       "rgba(255, 255, 255, .54)"}])))])))


(defn groups []
  (let [{:keys [dag-id]} (utils/context dag-context/context)
        {:keys [groups listen-events?]}
        (utils/query {:groups         [:dag dag-id :groups]
                      :listen-events? [:dag dag-id :listen-events?]})
        params {}]
    (into
     [:> Group {:listening listen-events?
                :key (count groups)}]
     (for [group-id groups]
       ^{:key group-id}
       [:f> group {:id group-id :params params}]))))


(def status-title
  {:success   "Done"
   :succeeded "Done"
   :warning   "Warning"
   :error     "Failed"
   :pending   "Pending"
   :default   "Not Scheduled"})

(defn node-tooltip []
  (let [{{:keys [open? content]} :tooltip} (utils/query {:tooltip [:node-tooltip]})]
    [tooltip/tooltip-root {:open open?}
     [tooltip/tooltip-content {:class "pointer-events-none bg-black/80 border-none text-white"
                               :side-offset 15}
      content]
     [tooltip/tooltip-trigger {:as-child true}
      [:div {:class "fixed pointer-events-none"
             :style @commons/tooltip-virtual-ref}]]]))

(defn open-tooltip [event text]
  (commons/open-tooltip event text))


(defn close-tooltip []
  (re-frame/dispatch [:dag/open-tooltip false]))


(def throttled-close-tooltip
  (throttle close-tooltip 100))


(defn node-primitive-view [{:keys [name node-height node-width dag-id color]}]
  (let [rect-ref (react/useRef nil)
        node-ref (react/useRef nil)]

    (react/useEffect
     (fn []

       (when-let [node (jsi/get node-ref :current)]
         (jsi/call node :cache))

       (when-let [rect (jsi/get rect-ref :current)]
         (jsi/call rect :to #js {:duration 0.4
                                 :opacity  1}))

       (fn return []
         (when-let [node (jsi/get node-ref :current)]
           (jsi/call node :clearCache))
         (throttled-close-tooltip))
       (fn []))
     #js [])

    [:<>
     [:> Group {:clipFunc (clip-right-fn {:width  50
                                          :height node-height
                                          :radius 12})
                :transformsEnabled "position"
                :perfectDrawEnabled false}
      [:> Rect {:ref rect-ref
                :listening false
                :width 50
                :fill color
                :height node-height
                :transformsEnabled "position"
                :perfectDrawEnabled false
                :x 0
                :y 0}]]

     [:> Group {:ref node-ref :listening false
                :transformsEnabled "position"
                :perfectDrawEnabled false}]]))


(defn node-simple-view [{:keys [name node-height node-width dag-id color]}]
  (let [rect-ref (react/useRef nil)
        node-ref (react/useRef nil)]

    (react/useEffect
     (fn []

       (when-let [node (jsi/get node-ref :current)]
         (jsi/call node :cache))

       (when-let [rect (jsi/get rect-ref :current)]
         (jsi/call rect :to #js {:duration 0.4
                                 :opacity  1}))

       (fn return []
         (when-let [node (jsi/get node-ref :current)]
           (jsi/call node :clearCache))
         (throttled-close-tooltip))
       (fn []))
     #js [])

    [:<>
     [:> Rect {:ref           rect-ref
               :width         node-width
               :height        node-height
               :transformsEnabled "position"
               :perfectDrawEnabled false
               :opacity       1
               :cornerRadius  14
               :on-mouseenter #(open-tooltip % name)
               :on-mouseleave #(close-tooltip)}]


     [:> Group {:ref node-ref :listening false

                :transformsEnabled "position"
                :perfectDrawEnabled false}
      [:> Group {:clipFunc (clip-right-fn {:width  11
                                           :height node-height
                                           :radius 12})
               :transformsEnabled "position"
               :perfectDrawEnabled false}
       [:> Rect {:width 11
                 :fill color
                 :height node-height
               :transformsEnabled "position"
               :perfectDrawEnabled false
                 :x 0
                 :y 0}]]


      [:> Rect {:width         50
                :height        10
                :fill          color
                :cornerRadius  5
               :transformsEnabled "position"
               :perfectDrawEnabled false
                :x 30
                :y 15}]


      [:> Rect {:width         (- node-width 100)
                :height        10
                :fill          "#CFD5DD"
               :transformsEnabled "position"
               :perfectDrawEnabled false
                :cornerRadius  5
                :x 30
                :y 50}]

      [:> Rect {:width         (- node-width 170)
                :height        10
                :fill          "#CFD5DD"
                :cornerRadius  5
               :transformsEnabled "position"
               :perfectDrawEnabled false
                :x 30
                :y 70}]]]))


(defonce transform-node-icon
  (new js/Image))
(jsi/assoc! transform-node-icon :src "/img/dag/transform.png")

(defonce extract-node-icon
  (new js/Image))
(jsi/assoc! extract-node-icon :src "/img/dag/extract.png")

(def short-duration
  (.humanizer humanizeDuration
              (clj->js {:language "shortEn"
                        :languages {"shortEn" {:y #(-> "y")
                                               :mo #(-> "mo")
                                               :w #(-> "w")
                                               :d #(-> "d")
                                               :h #(-> "h")
                                               :m #(-> "m")
                                               :s #(-> "s")
                                               :ms #(-> "ms")}}})))

(defn node-full-view [{:keys [id name type node-height node-width
                              drawing-edge? color title selected?
                              status scale transform]}]
  (let [node-ref (react/useRef nil)]

    (react/useEffect
     (fn []
       (when-let [node (jsi/get node-ref :current)]
         (jsi/call node :cache))


       (fn return []
         (when-let [node (jsi/get node-ref :current)]
           (jsi/call node :clearCache)))))

    [:> Group {:ref node-ref
               :listening false}

     [:> Group {:clipFunc (clip-right-fn {:width  11
                                          :height node-height
                                          :radius 16})}
      [:> Rect {:width 11
                :fill color
                :height node-height
                :transformsEnabled "position"
                :perfectDrawEnabled false
                :x 1
                :y 1}]]

     ;; Node details
     [:> Group {:x 11 :y 0}

      [:> Text {:x 12
                :y 10
                :fill color
                :font-size 13
                :font-style 600
                :transformsEnabled "position"
                :perfectDrawEnabled true

                :ellipsis  true
                :wrap      "char"
                :width     (- node-width 20 10)
                :height    20
                :text      (app.utils/format-transform-status status (:status transform))}]

      [:> Text {:x -15
                :y 10
                :fill (:default dag-palette)
                :font-size 13
                :font-style 600
                :transformsEnabled "position"
                :perfectDrawEnabled true

                :ellipsis  true
                :wrap      "char"
                :width     (- node-width 20 10)
                :height    20
                :align     "right"
                :text      (case type
                             :transform "Transform"
                             :extract   "Extract"
                             nil)}]

      [:> Image {:x (- node-width 40)
                 :y 6
                 :width 20
                 :height 20
                 :align "right"
                 :image (case type
                          :transform transform-node-icon
                          :extract   extract-node-icon
                          nil)}]

      [:> Rect {:x 0 :y 30
                :fill "#CFD5DD"
                :transformsEnabled "position"
                :perfectDrawEnabled false
                :height 1
                :width  (- node-width 11)}]

      [:> Text {:text      name
                :transformsEnabled "position"
                :perfectDrawEnabled true
                :x         12
                :y         43
                :font-size 16
                :width     (- node-width 11 12 10)
                :line-height 1.25
                :align     "left"
                :wrap      "char"
                :fill      "black"}]]

     ;; Connection points
     #_(when (or selected? drawing-edge?)
         [:<>
          [:f> input-circle
           {:selected? selected?}]
          [:f> connection-circle
           {:id        id
            :selected? selected?}]])]))



(defn visible? [{:keys [x y stage node-width node-height]}]
  ;; (let [rect  (jsi/call stage :getClientRect)
  ;;       scale (jsi/call stage :scaleX)
  ;;       sx    (jsi/get rect :x)
  ;;       sy    (jsi/get rect :y)
  ;;
  ;;       scaled-width  (/ (jsi/call stage :width) scale)
  ;;       scaled-height (/ (jsi/call stage :height) scale)
  ;;
  ;;       tl {:x (- sx)
  ;;           :y (- sy)}
  ;;
  ;;       br {:x (+ scaled-width (- sx))
  ;;           :y (+ scaled-height (- sy))}
  ;;
  ;;       in-viewport? (and (> x (:x tl))
  ;;                         (< x (:x br))
  ;;                         (> y (:y tl))
  ;;                         (< y (:y br)))]
  ;;
  ;;   in-viewport?)

  true)

(defn extract-statuses [status]
  (case (some-> status name)
    "running" "pending"
    "success" "succeeded"
    "fail"   "failed"
    status))

(defn job->status [{:extract-job/keys [status start-time end-time] :as job}]
  {:started-at  start-time
   :finished-at end-time
   :status      (extract-statuses status)})

(defn extract->status [{:keys [last-run-status last-run-at]}]
  {:last-run-at last-run-at
   :status      (extract-statuses last-run-status)})

(defn node [{:keys [id x y status type node-height node-width
                    simple-view? primitive-view? drawing-edge? last-index
                    dag-id stage connected-nodes selected-node
                    statuses extract-jobs stage scale transform extract]

             {:keys [id transform-id master-id]} :transform

             :as   node-props}]
  (let [selected?     (= id selected-node)
        connected?    (and (some? selected-node)
                           (contains? connected-nodes id))
        highlighted?  (or selected? connected? #_(nil? selected-node))
        entity        (or transform extract)
        transform?    (= type :transform)
        displayed?    (visible? node-props)

        status       (if transform?
                       (minoro.utils/find-first #(= (:transform-id %) (or transform-id id)) statuses)
                       (or (some-> extract-jobs
                                   (minoro.utils/find-by-field :extract-job/extract (:extract-id entity))
                                   job->status)
                           (extract->status entity)))

        execution-status (:status status)

        color        (get dag-palette execution-status (:default dag-palette))

        title        (get status-title status)
        node-props'  (merge node-props {:color color :title title :selected? selected? :status status})]

    (when displayed?
      [:> Group {:x              x
                 :y              y
                 :id             id
                 :name           "node"
                 :draggable      true

                 :transformsEnabled "position"
                 :perfectDrawEnabled false

                 :on-drag-move   #(on-move % last-index id)
                 :on-drag-end    #(stop-move % id)
                 :on-click       #(commons/select-node stage dag-id id)
                 :on-mouseenter  #(when drawing-edge? (commons/select-node stage dag-id id))
                 :on-mouseleave  #(when drawing-edge? (unselect-node dag-id))
                 :on-contextmenu #(open-node-context % id)}

       ;; Outer layout
       [:> Rect {:width        node-width
                 :height       node-height
                 :stroke       "#CFD5DD"
                 :transformsEnabled "position"
                 :perfectDrawEnabled false
                 :cornerRadius 12}]
       (when selected?
         [:> Rect {:width        node-width
                   :height       node-height
                   :stroke       "#9BCDEA"
                   :transformsEnabled "position"
                   :perfectDrawEnabled false
                   :cornerRadius 12}])
       [:> Rect {:x            1
                 :y            1
                 :width        (- node-width 2)
                 :height       (- node-height 2)
                 :fill         "#FFFFFF"
                 :transformsEnabled "position"
                 :perfectDrawEnabled false
                 :cornerRadius 12

                 :on-mouseenter (when simple-view? #(open-tooltip % (:name node-props')))
                 :on-mouseleave #(close-tooltip)}]

       (cond

         (not displayed?)
         nil

         primitive-view?
         [:f> node-primitive-view node-props']

         simple-view?
         [:f> node-simple-view node-props']

         :else
        ;; full view
         [:f> node-full-view node-props'])])))


(defn nodes [{:keys [stage-ref]}]
  (let [stage            (jsi/get stage-ref :current)
        {:keys [dag-id]} (utils/context dag-context/context)
        {:keys [nodes scale selected-node drawing-edge? node-width node-height connected-nodes listen-events? pos-x pos-y]}
        (utils/query {:nodes           [:dag dag-id {:nodes [:jobs]}]
                      :scale           [:dag dag-id :scale]
                      :pos-x           [:dag dag-id :x]
                      :pos-y           [:dag dag-id :y]
                      :selected-node   [:dag dag-id :selected-node]
                      :drawing-edge?   [:dag dag-id :drawing-edge?]
                      :connected-nodes [:dag dag-id :connected-nodes]
                      :listen-events?  [:dag dag-id :listen-events?]
                      :node-width      [:dag-params :node-width]
                      :node-height     [:dag-params :node-height]})
        statuses     @(re-posh/subscribe [:transform-status-list/prop :transform-status-list/statuses])
        extract-jobs @(re-posh/subscribe [:extract-job/pull-many])

        last-index (- (count nodes) 1)

        simple-view?    (< scale 0.4)
        primitive-view? (< scale 0.2)]

    (into
     [:> Group {:listening listen-events?}]
     (for [{:keys [id] :as node-props} nodes]
       [node (merge node-props
                    {:node-height     node-height
                     :node-width      node-width
                     :drawing-edge?   drawing-edge?
                     :last-index      last-index
                     :dag-id          dag-id
                     :stage           stage
                     :simple-view?    simple-view?
                     :primitive-view? primitive-view?
                     :connected-nodes connected-nodes
                     :selected-node   selected-node
                     :pos-x           pos-x
                     :pos-y           pos-y
                     :scale           scale
                     :statuses        statuses
                     :extract-jobs    extract-jobs})]))))
